import type { LoadingState } from '@ipfabric/ui-components/utils/loading/withLoadingIndicator';
import {
  type AttributeFilters,
  type AvailableGraphEntities,
  type DeviceGroupAttribute,
  type EdgeId,
  FactoryPathLookupPreset,
  FactoryPreset,
  type GraphEdgeSettings,
  type GraphOverlay,
  type GraphPath,
  type GraphPathInfo,
  type GraphPathLookupParameters,
  type GraphTechnologies,
  type GraphTopologyParameters,
  type GraphView,
  type GraphViewId,
  type GraphViewPreviewable,
  type GroupEdgeSettings,
  type GroupEdgeSettingsId,
  type Layout,
  type NamedPathLookupAdvancedOptions,
  type NodeData,
  type NodeId,
  type NodeSn,
  type PathLookupDeviceDecisionsMap,
  type PathLookupEventsSummary,
  type PathLookupGraphPreset,
  type PathLookupPassingTraffic,
  type Point,
  type ProtocolEdgeType,
  type SnapshotId,
  type TopologyEdgeSettings,
  type TopologyGraphPreset,
  factoryPathLookupAdvancedOptions,
  isPathLookupRequestParameters,
  isTopologyRequestParameters,
} from '@ipfabric/utils';
import type { PathLookupPathInspectorSelectedPackets } from 'components/DeviceView/deviceViewTypes';
import { initialPathLookupAdvancedOptions } from 'components/Graph/GraphSidePanel/GraphPathlookup/PathLookupAdvancedOptionsFormValues';
import type { GraphSidePanelData } from 'components/Graph/GraphSidePanelData';
import type { GraphTooltipPayload } from 'components/Graph/GraphTooltip/GraphTooltipPayload';
import {
  type GraphData,
  emptyGraphData,
} from 'components/Graph/model/GraphData';
import { generateGuid } from 'utils/guid';
import {
  type CallbackHistory,
  emptyCallbackHistory,
} from 'utils/history/CallbackHistory';
import type { SelectorCache } from 'utils/react/scopedSelector';

export const isTopologyProtocolSettings = (
  item: GraphEdgeSettings,
): item is TopologyEdgeSettings => item.type === 'protocol';

export const isGroupSettings = (
  edge: GraphEdgeSettings,
): edge is GroupEdgeSettings => edge.type === 'group';

export type PresetDraftState =
  | 'adding'
  | 'deleting'
  | 'updating'
  | 'resetting'
  | 'idle';

export interface ProtocolSettingsWindow {
  open: boolean;
  groupId: GroupEdgeSettings['id'] | null;
  protocolName?: ProtocolEdgeType;
}

export interface GraphLayoutSettings {
  draggedGroupId: GroupEdgeSettingsId | null;
  draggedProtocol: ProtocolEdgeType | null;
  presetDraft: TopologyGraphPreset;
  presetDraftState: PresetDraftState;
  presetEditMode: boolean;
  preset: TopologyGraphPreset;
  presets: TopologyGraphPreset[];
}

export interface EdgeContextMenu {
  edgeId: EdgeId;
  position: Point;
}

export interface NodeContextMenu {
  nodeId: NodeId;
  position: Point;
}

export interface GraphLeavingDialog {
  continueCallback: (() => void) | null;
  open: boolean;
}

export interface LoadViewDialog {
  loadedView: GraphView | null;
  open: boolean;
  viewIdsBeingStarred: Set<GraphViewId>;
  viewIdsBeingUnstarred: Set<GraphViewId>;
  views: GraphViewPreviewable[];
}

export interface RefreshSnapshotDialog {
  discoveryStarting: boolean;
  open: boolean;
}

export interface PathLookupPathInspectorHighlightedEdge {
  incomingEdges: EdgeId[];
  outgoingEdges: EdgeId[];
}

export type PathLookupPathInspectorHighlightedEdges = {
  [Key in NodeSn]: PathLookupPathInspectorHighlightedEdge;
};

export type PathLookupPathInspectorEnlargedEdges = {
  [Key in NodeSn]: EdgeId[];
};

export interface GraphTooltip {
  moveWithMouse: boolean;
  payload: GraphTooltipPayload;
  position: {
    // If is shown opposite tooltip, show at inverseY position
    inverseY?: number;
    x: number;
    y: number;
  };
  verticalOffset?: number;
}

export interface Pathlookup {
  advancedOptions: NamedPathLookupAdvancedOptions[];
  selectedAdvancedOptions: NamedPathLookupAdvancedOptions;
  pathInspector: {
    activeNodeSn: NodeSn | null;
    enlargedEdges: PathLookupPathInspectorEnlargedEdges;
    highlightedEdges: PathLookupPathInspectorHighlightedEdges;
    hoveredEdge: EdgeId | null;
    selectedPackets: PathLookupPathInspectorSelectedPackets;
  };
  presetDraft: PathLookupGraphPreset;
  preset: PathLookupGraphPreset;
}

export interface SearchResult {
  edgeIds: Set<EdgeId>;
  nodeExactMatch: NodeData | null;
  nodeIds: Set<NodeId>;
}

interface Technologies {
  selectedTechnologies: GraphTechnologies;
}

export type DeviceGroupAttributePathLayouts = {
  [Path in GraphPath]?: Layout;
};

export type PathLayouts = {
  [GroupBy in DeviceGroupAttribute]: DeviceGroupAttributePathLayouts;
};

interface GraphLayoutDialog {
  nodeSns?: NodeSn[];
  path: GraphPath;
}

interface GraphMetadataBase {
  /**
   * ID of the snapshot current graph was loaded with
   *
   * it may differ from state.snapshotId because:
   * last change of snapshot didn't lead to loading new graph (e.g. because we tried to compare same snapshots)
   * state.snapshotId is set immediately before new graph is loaded, so it remains inconsistent during loading
   */
  snapshotId: SnapshotId;
}

export interface TopologyGraphMetadata extends GraphMetadataBase {
  availableEntities: AvailableGraphEntities;
  /**
   * Set of paths which have nodes repositioned compared to their position originally retrieved
   * from server, which indicates there is a change in layout that can be saved as an user-defined layout.
   *
   * Note that the information here may not be exact - for example we don't reset this flag when user
   * moves a node forth and back to its original position. However false negatives shouldn't be here
   * - if there are changes in a path, it should be always present here.
   */
  nodesRepositioned: Set<GraphPath>;
  parameters: GraphTopologyParameters;
  /** Set of paths from the request that exist, even those no nodes were returned for */
  paths: Record<GraphPath, GraphPathInfo>;
  /** All paths available in snapshot are selected, so nothing is left for IPF_NETWORK_VIEWER path */
  allPathsSelected: boolean;
}

export interface PathLookupGraphMetadata extends GraphMetadataBase {
  parameters: GraphPathLookupParameters;
  decisions: PathLookupDeviceDecisionsMap;
  initialExpectedPassingTraffic: PathLookupPassingTraffic | null;
  passingTraffic: PathLookupPassingTraffic;
  eventsSummary: PathLookupEventsSummary;
}

export type GraphMetadata = TopologyGraphMetadata | PathLookupGraphMetadata;

export const isTopologyGraphMetadata = (
  metadata?: GraphMetadata | null,
): metadata is TopologyGraphMetadata =>
  isTopologyRequestParameters(metadata?.parameters);
export const isPathLookupGraphMetadata = (
  metadata?: GraphMetadata | null,
): metadata is PathLookupGraphMetadata =>
  isPathLookupRequestParameters(metadata?.parameters);

interface NetworkViewer {
  groupBy: DeviceGroupAttribute | null;
}

export interface GraphState {
  attributeFilters: AttributeFilters;
  /**
   * True until first content is loaded to the graph component
   */
  defaultPathLayouts: PathLayouts;
  edgeContextMenu: EdgeContextMenu | null;
  firstLoading: boolean;
  graph: GraphData;
  graphLayoutDialog: GraphLayoutDialog | null;
  /**
   * This field should contain all state with lifecycle bound to currently loaded graph.
   * It should guide us to properly clear not any more relevant fields when a different graph
   * is loaded.
   *
   * Note that some fields may seem relevant to currently loaded graph only, e.g. graph preset
   * or topology path layouts, however these fields are also used in side panel when no
   * graph is loaded yet, so they can't be put here.
   */
  graphMetadata: GraphMetadata | null;
  highlightedNodeId: NodeId | null;
  history: CallbackHistory;
  layoutSettings: GraphLayoutSettings;
  leavingDialog: GraphLeavingDialog;
  loading: LoadingState;
  /**
   * GUID updated with each initiated graph loading, including loading empty graph
   *
   * This serves to ensure no overlaying loading requests exist - when
   * request B to load graph is fired before running request A finishes,
   * request A is discarded and only request B is applied.
   */
  loadingVersion: string;
  loadViewDialog: LoadViewDialog;
  /** Show loading indicator when rerenders are expected to be long (load/update whole graph etc) */
  longRendering: boolean;
  longRenderingLocked: boolean;
  networkViewer: NetworkViewer;
  nodeContextMenu: NodeContextMenu | null;
  overlay: GraphOverlay | null;
  pathLayouts: PathLayouts;
  pathlookup: Pathlookup;
  peekingAtHiddenNodes: boolean;
  refreshSnapshotDialog: RefreshSnapshotDialog;
  searchResult: SearchResult | null;
  selectorCache: SelectorCache;
  sharedViewId: string | null;
  sidePanel: GraphSidePanelData;
  snapshotId: SnapshotId;
  tooltip: GraphTooltip | null;
  technologies: Technologies;
  emptyGraphReason: string | null;
}

export const initialTechnologies = {
  expandDeviceGroups: [],
  stpInstances: {
    isolate: false,
    instances: [],
  },
};

const emptyPathLayouts: PathLayouts = {
  region: {},
  routingDomain: {},
  siteName: {},
  stpDomain: {},
};

export const emptyAvailableEntities = {
  edgeSettingsIds: [],
  deviceTypes: [],
};

export const createGraphState = ({
  snapshotId,
  attributeFilters,
}: Pick<GraphState, 'snapshotId' | 'attributeFilters'>): GraphState => ({
  attributeFilters,
  defaultPathLayouts: emptyPathLayouts,
  edgeContextMenu: null,
  firstLoading: true,
  graph: emptyGraphData,
  graphLayoutDialog: null,
  graphMetadata: null,
  highlightedNodeId: null,
  history: emptyCallbackHistory,
  layoutSettings: {
    draggedGroupId: null,
    draggedProtocol: null,
    presetEditMode: false,
    preset: FactoryPreset,
    presetDraft: FactoryPreset,
    presetDraftState: 'idle',
    presets: [],
  },
  leavingDialog: {
    continueCallback: null,
    open: false,
  },
  loading: 'INITIAL',
  loadingVersion: generateGuid(),
  loadViewDialog: {
    loadedView: null,
    open: false,
    viewIdsBeingStarred: new Set(),
    viewIdsBeingUnstarred: new Set(),
    views: [],
  },
  longRendering: false,
  longRenderingLocked: false,
  networkViewer: {
    groupBy: null,
  },
  nodeContextMenu: null,
  overlay: null,
  pathLayouts: emptyPathLayouts,
  pathlookup: {
    advancedOptions: factoryPathLookupAdvancedOptions,
    selectedAdvancedOptions: initialPathLookupAdvancedOptions,
    pathInspector: {
      activeNodeSn: null,
      enlargedEdges: {},
      highlightedEdges: {},
      hoveredEdge: null,
      selectedPackets: {},
    },
    presetDraft: FactoryPathLookupPreset,
    preset: FactoryPathLookupPreset,
  },
  peekingAtHiddenNodes: false,
  refreshSnapshotDialog: {
    discoveryStarting: false,
    open: false,
  },
  searchResult: null,
  selectorCache: new Map(),
  sharedViewId: null,
  sidePanel: {
    open: false,
    contentId: 'networkViewer',
    intentVerificationTabId: 'defined-rules',
    networkViewerTabId: 'dataInput',
    pathLookupTabId: 'dataInput',
    pathLookupDataInputTabId: 'unicast',
    pathLookupVisualizationSetupTabId: 'edgeColors',
    topologyLayoutSettingsTabId: 'protocols',
  },
  snapshotId,
  tooltip: null,
  technologies: {
    selectedTechnologies: initialTechnologies,
  },
  emptyGraphReason: null,
});
